import { FC, useEffect, useState } from 'react';
import styles from './index.less';
import { Checkbox, Col, Form, InputNumber, Row, Select, Space } from 'antd';
import { useRequest, useUpdateEffect } from 'ahooks';
import { getCampNodes } from '@/services/ConsoleRequest';
import { useParams } from 'umi';
import { TConsoleSetingsNode } from '@/types/console/node';
import { cloneDeep, isEmpty, isEqual, isUndefined } from 'lodash';
import cx from 'classnames';

export type TRewardConditionRowType =
  | 'selectedEarnCredits'
  | 'selectedSuccessForCard'
  | 'selectedFinishNodes'
  | 'selectedRank';

type TRewardConditionValueNameType =
  | 'earnCredits'
  | 'finishNodes'
  | 'minRankAfterFinishCamp'
  | 'maxRankAfterFinishCamp';

type TConditionDataKey = TRewardConditionRowType | TRewardConditionValueNameType;

type TConditionData = Partial<Record<TConditionDataKey, any>>;

export const sourceData: Record<
  TRewardConditionRowType,
  { label: string; valueName?: TRewardConditionValueNameType | TRewardConditionValueNameType[] }
> = {
  selectedSuccessForCard: {
    label: '打卡成功',
  },
  selectedEarnCredits: {
    label: '获得学分',
    valueName: 'earnCredits',
  },
  selectedFinishNodes: {
    label: '完成节点',
    valueName: 'finishNodes',
  },
  selectedRank: {
    label: '结营后排名',
    valueName: ['minRankAfterFinishCamp', 'maxRankAfterFinishCamp'],
  },
};

const RewardConditionRow: FC<{
  value?: TConditionData;
  type: TRewardConditionRowType;
  text: string;
  isError?: boolean;
  valueName?: TRewardConditionValueNameType | TRewardConditionValueNameType[];
  onChange: (data: Partial<Record<TRewardConditionValueNameType, any>>) => void;
  onCheck: (type: TRewardConditionRowType, checked: boolean) => void;
}> = ({ isError, value = {}, type, onCheck, text, onChange, valueName }) => {
  const { minRankAfterFinishCamp, maxRankAfterFinishCamp } = value;
  const [checked, setChecked] = useState(false);
  const [nodeData, setNodeData] = useState<TConsoleSetingsNode[]>([]);
  const [scoreArray, setScoreArray] = useState([0, 0]);
  const { id: campId } = useParams<{ id: string }>();
  const { runAsync } = useRequest(
    () => {
      return getCampNodes(campId);
    },
    {
      manual: true,
      onSuccess: (res) => {
        if (res?.code === 200) {
          if (Array.isArray(res.data)) {
            setNodeData(res.data);
          }
        }
      },
    },
  );
  const renderComByType = () => {
    switch (type) {
      case 'selectedSuccessForCard':
        return null;
      case 'selectedEarnCredits':
        return (
          <InputNumber
            status=""
            precision={0}
            min={0}
            addonBefore="≥"
            disabled={!checked}
            defaultValue={value?.[valueName as TRewardConditionRowType]}
            addonAfter="学分"
            onChange={(value) => {
              onChange({ [valueName as TRewardConditionRowType]: value });
            }}
          />
        );
      case 'selectedFinishNodes':
        return (
          <Select
            mode="multiple"
            disabled={!checked}
            loading={isEmpty(nodeData)}
            defaultValue={value?.[valueName as TRewardConditionRowType]}
            onChange={(value) => {
              onChange({ [valueName as TRewardConditionRowType]: value });
            }}
            options={nodeData.map((item) => ({ value: item.id, label: item.name }))}
          />
        );
      case 'selectedRank':
        return (
          <Space>
            <InputNumber
              onChange={(value) => {
                const newArray = [value || 0, scoreArray[1]];
                handleScoreChange(newArray);
                setScoreArray(newArray);
              }}
              defaultValue={value?.minRankAfterFinishCamp}
              precision={0}
              min={0}
              addonAfter="名"
              disabled={!checked}
            />
            <span>
              {'≤'}排名{'≤'}
            </span>
            <InputNumber
              onChange={(value) => {
                const newArray = [scoreArray[0], value || 0];
                handleScoreChange(newArray);
                setScoreArray(newArray);
              }}
              defaultValue={value?.maxRankAfterFinishCamp}
              precision={0}
              min={0}
              addonAfter="名"
              disabled={!checked}
            />
          </Space>
        );
    }
  };
  const renderCheckbox = () => {
    return (
      <Checkbox
        onChange={(e) => {
          const tempChecked = e.target.checked;
          setChecked(tempChecked);
          onCheck(type, tempChecked);
        }}
        defaultChecked={value?.[type]}
        className={styles.create_reward_rowlabel}
      >
        {text}
      </Checkbox>
    );
  };
  useEffect(() => {
    if (type === 'selectedFinishNodes') {
      runAsync();
    }
  }, [type]);
  const handleScoreChange = (tempScoreArray: number[]) => {
    if (tempScoreArray.length === 2 && type === 'selectedRank' && Array.isArray(valueName)) {
      let obj: Record<'minRankAfterFinishCamp' | 'maxRankAfterFinishCamp', number> = {
        maxRankAfterFinishCamp: 0,
        minRankAfterFinishCamp: 0,
      };
      for (let i = 0; i < valueName.length; i++) {
        const key = valueName[i];
        obj[key as 'minRankAfterFinishCamp' | 'maxRankAfterFinishCamp'] = tempScoreArray[i];
      }
      onChange(obj);
    }
  };
  useEffect(() => {
    if (
      !isUndefined(minRankAfterFinishCamp) &&
      !isUndefined(maxRankAfterFinishCamp) &&
      !isEqual(scoreArray, [minRankAfterFinishCamp, maxRankAfterFinishCamp])
    ) {
      console.log('要设置了', scoreArray);
      setScoreArray([minRankAfterFinishCamp, maxRankAfterFinishCamp]);
    }
  }, [minRankAfterFinishCamp, maxRankAfterFinishCamp, scoreArray]);

  useEffect(() => {
    setChecked(value?.[type]);
  }, [value]);
  return (
    <Row className={cx(styles.condition_row, isError && styles.condition_row_error)}>
      <Col span={4}>{renderCheckbox()}</Col>
      <Col span={20}>{renderComByType()}</Col>
    </Row>
  );
};

const RewardCondition: FC<{
  defaultValue?: TConditionData;
  onChange?: (data: TConditionData) => void;
  value?: TConditionData;
}> = (props) => {
  const form = Form.useFormInstance();
  const condition_error = Form.useWatch('condition_error', form);
  const { onChange, value, defaultValue } = props;
  const [errorInfo, setErrorInfo] = useState({
    selectedSuccessForCard: false,
    selectedEarnCredits: false,
    selectedFinishNodes: false,
    selectedRank: false,
  });
  const [conditionData, setConditionData] = useState<TConditionData>({});
  const renderRow = () => {
    const res = [];
    for (const key in sourceData) {
      const field = key as TRewardConditionRowType;
      res.push(
        <RewardConditionRow
          value={value}
          valueName={sourceData[field].valueName}
          text={sourceData[field].label}
          type={field}
          isError={errorInfo[field]}
          onChange={(data) => {
            console.log('data是');
            console.log(data);
            setConditionData((prevState) => {
              return { ...prevState, ...data };
            });
          }}
          onCheck={(type, checked) => {
            setConditionData((prevState) => {
              prevState[type] = checked;
              return { ...prevState };
            });
          }}
        />,
      );
    }
    return res;
  };
  const calcError = (value: TConditionData) => {
    let errorObj = cloneDeep(errorInfo);
    if (value?.selectedEarnCredits) {
      if (!value.earnCredits) {
        errorObj = { ...errorObj, selectedEarnCredits: true };
      } else {
        errorObj = { ...errorObj, selectedEarnCredits: false };
      }
    } else {
      errorObj = { ...errorObj, selectedEarnCredits: false };
    }

    if (value?.selectedFinishNodes) {
      if (isEmpty(value.finishNodes)) {
        errorObj = { ...errorObj, selectedFinishNodes: true };
      } else {
        errorObj = { ...errorObj, selectedFinishNodes: false };
      }
    } else {
      errorObj = { ...errorObj, selectedFinishNodes: false };
    }
    if (value.selectedRank) {
      if (
        value?.minRankAfterFinishCamp !== undefined &&
        value?.maxRankAfterFinishCamp !== undefined
      ) {
        if (value.minRankAfterFinishCamp > value.maxRankAfterFinishCamp) {
          errorObj = { ...errorObj, selectedRank: true };
        } else {
          errorObj = { ...errorObj, selectedRank: false };
        }
      } else {
        errorObj = { ...errorObj, selectedRank: true };
      }
    } else {
      errorObj = { ...errorObj, selectedRank: false };
    }
    setErrorInfo(errorObj);
    console.log('最后的errorObj', errorObj);
  };
  useUpdateEffect(() => {
    console.log('conditionData变化');
    console.log(conditionData);
    onChange?.(conditionData);
    calcError(conditionData);
  }, [conditionData]);
  useEffect(() => {
    if (defaultValue) {
      setConditionData(defaultValue);
    }
  }, [defaultValue]);
  return (
    <div className={styles.create_reward_condition}>
      {renderRow()}
      <div style={{ color: '#8d8d8d' }}>启用的多个条件之间为且关系</div>
    </div>
  );
};
export default RewardCondition;
